<script setup>
    import { computed, ref, watch } from 'vue'
    import { storeToRefs } from 'pinia'
    import { useMenuStore } from '~~/stores/menu'

    function getMeta(route, name) {
        return getRouteMeta(route, name)
    }

    const router = useRouter()
    const route = useRoute()

    const menuStore = useMenuStore()
    await menuStore.fetchMenu()
    const { menu: serverItems } = storeToRefs(menuStore)

    const TEMPLATE_SEGMENT_PREFIX = ':'

    function isTemplatedPath(path) {
        const segments = path.split('/')
        return segments.some((segment) => segment.startsWith(TEMPLATE_SEGMENT_PREFIX))
    }

    function withoutTemplatedChildren(item) {
        if (!item.children) {
            return item
        }
        const pageChildren = item.children.filter((child) => !isTemplatedPath(child.path))
        if (item.children.length > 0 && pageChildren.length === 0) {
            const { children, ...flatItem } = item
            return flatItem
        }
        return {
            ...item,
            children: pageChildren,
        }
    }

    const menuItems = computed(() => {
        const routerItems = [...router.options.routes]
            .filter((route) => route.meta?.menu)
            .map((route) => ({
                path: route.path,
                title: getMeta(route, 'title'),
                icon: getMeta(route, 'icon'),
                position: route.meta?.position ?? 999,
            }))

        const serverMenuItems = (serverItems.value ?? []).map(withoutTemplatedChildren)
        const merged = [
            ...routerItems,
            ...serverMenuItems,
        ]

        return merged.sort((a, b) => a.position - b.position)
    })

    const expandedPaths = ref(new Set())

    watch(() => route.path, (newPath) => {
        menuItems.value.forEach((item) => {
            if (item.children && newPath.startsWith(item.path)) {
                expandedPaths.value = new Set([...expandedPaths.value, item.path])
            }
        })
    }, { immediate: true })

    function isExpanded(item) {
        return expandedPaths.value.has(item.path)
    }

    function setExpanded(item, val) {
        const next = new Set(expandedPaths.value)
        if (val) {
            next.add(item.path)
        } else {
            next.delete(item.path)
        }
        expandedPaths.value = next
    }
</script>

<template>
    <div class="nav-container">
        <q-toolbar class="bg-secondary text-white">
            <q-toolbar-title>Boundless Tower</q-toolbar-title>
        </q-toolbar>

        <div class="nav-scroll">
            <q-list>
                <template
                    v-for="item in menuItems"
                    :key="item.path"
                >
                    <q-expansion-item
                        v-if="item.children"
                        :icon="item.icon"
                        :label="item.title"
                        :to="item.path"
                        :model-value="isExpanded(item)"
                        expand-separator
                        @update:model-value="setExpanded(item, $event)"
                    >
                        <q-item
                            v-for="child in item.children"
                            :key="child.path"
                            clickable
                            :to="child.path"
                            class="q-pl-xl"
                        >
                            <q-item-section avatar>
                                <q-icon :name="child.icon" />
                            </q-item-section>
                            <q-item-section>
                                <q-item-label>{{ child.title }}</q-item-label>
                            </q-item-section>
                        </q-item>
                    </q-expansion-item>

                    <q-item
                        v-else
                        clickable
                        :to="item.path"
                    >
                        <q-item-section avatar>
                            <q-icon :name="item.icon" />
                        </q-item-section>
                        <q-item-section>
                            <q-item-label>{{ item.title }}</q-item-label>
                        </q-item-section>
                    </q-item>
                </template>
            </q-list>
        </div>
    </div>
</template>

<style scoped>
.nav-container {
    display: flex;
    flex-direction: column;
    height: 100%;
}
.nav-scroll {
    overflow-y: auto;
    flex: 1;
}
</style>
